// API Configuration
const API_BASE_URL = 'https://unified-api.cungvedich.vn/api';
const ORGANIZATION_ID = 4;
const API_TIMEOUT = 20000; // 20 seconds

// Timeout utility function
const fetchWithTimeout = async (url, options = {}, timeout = API_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Kết nối quá chậm. Vui lòng thử lại sau (timeout 20s).');
    }
    throw error;
  }
};

// Helper method to get access token from sessionStorage
const getAccessToken = () => {
  return sessionStorage.getItem('accessToken');
}

// Helper method to create headers with automatic token injection
const createHeaders = (customHeaders = {}) => {
  const headers = {
      'Content-Type': 'application/json',
      ...customHeaders
  };
  
  const accessToken = getAccessToken();
  if (accessToken) {
      headers['x-access-token'] = accessToken;
  }
  
  return headers;
}

// API functions with real endpoints
const API = {
  
  // Check if user exists by phone number and return user info
  checkUserExists: async (phoneNumber, organizationId = null) => {
    try {
      console.log('Checking user exists for phone:', phoneNumber, 'with organizationId:', organizationId || ORGANIZATION_ID);
      
      const response = await fetchWithTimeout(`${API_BASE_URL}/auth/login-with-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phoneNumber,
          organizationId: organizationId || ORGANIZATION_ID
        })
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        // 2xx status codes - User exists
        try {
          const responseData = await response.json();
          const data = responseData.data;
          console.log('User exists - Response data:', data);
          

          // Return user info if available, otherwise just true
          if (data && data.accessToken) {
            return {
              exists: true,
              userInfo: data.userInfo,
              accessToken: data.accessToken
            };
          }
          return { 
            exists: true, 
            userInfo: null,
            accessToken: data.accessToken
          };
        } catch (parseError) {
          console.warn('Could not parse JSON for 2xx response, but user exists:', parseError);
          return { 
            exists: true, 
            userInfo: null,
            accessToken: null
          };
        }
      } else if (response.status === 404 || response.status === 401) {
        // User does not exist (404) or unauthorized (401) - both mean user not found
        console.log(`=== API ERROR DEBUG ===`);
        console.log(`User not found (${response.status}) - redirecting to registration`);
        console.log(`Response status: ${response.status}`);
        console.log(`Response ok: ${response.ok}`);
        console.log(`Response url: ${response.url}`);
        
        const errorMessage = `API Error (${response.status}): User not found`;
        console.log(`Throwing error: ${errorMessage}`);
        throw new Error(errorMessage);
      } else {
        // Other error - try to get error details
        console.log('Other error status:', response.status);
        try {
          const errorData = await response.json();
          console.error('API Error data:', errorData);
          throw new Error(`API Error (${response.status}): ${errorData.message || 'Có lỗi xảy ra khi kiểm tra số điện thoại'}`);
        } catch (parseError) {
          console.error('Could not parse error response:', parseError);
          throw new Error(`API Error (${response.status}): Có lỗi xảy ra khi kiểm tra số điện thoại`);
        }
      }
    } catch (error) {
      // Only network errors should reach here now
      if (error.message.includes('API Error')) {
        // Re-throw API errors
        throw error;
      } else {
        console.error('Network error:', error);
        throw new Error('Không thể kết nối đến server. Vui lòng thử lại.');
      }
    }
  },

  // Get detailed user information by phone number
  getUserInfo: async (phoneNumber) => {
    try {
      const headers = createHeaders();
      console.log('Getting user info for phone:', phoneNumber);
      
      const response = await fetchWithTimeout(`${API_BASE_URL}/users/profile`, {
        method: 'GET',
        headers: headers
      });

      if (response.ok) {
        const data = await response.json();
        console.log('User info response:', data);
        
        const userInfo = data.data;
        
        return userInfo;

      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get user info error:', errorData);
        throw new Error(`Không thể lấy thông tin người dùng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get user info network error:', error);
      throw new Error('Không thể kết nối đến server để lấy thông tin người dùng.');
    }
  },

  // Upload image file
  uploadImage: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', file.name || 'avatar.jpg');

      const response = await fetchWithTimeout(`${API_BASE_URL}/documents/file`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        // 2xx status codes - Success
        const data = await response.json();
        console.log('Upload response:', data);
        
        // Handle different response formats
        if (data.statusCode >= 200 && data.statusCode < 300 && data.data && data.data.url) {
          return {
            success: true,
            fileUrl: data.data.url,
            fileId: data.data.id,
            fileName: data.data.fileName || file.name
          };
        } else if (data.url) {
          // Direct response format
          return {
            success: true,
            fileUrl: data.url,
            fileId: data.id,
            fileName: data.fileName || file.name
          };
        } else {
          console.warn('Unexpected upload response format:', data);
          throw new Error('Upload response format error');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Upload error:', errorData);
        throw new Error(`Upload failed (${response.status}): ${errorData.message || 'Không thể upload file. Vui lòng thử lại.'}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error('Có lỗi xảy ra khi upload file: ' + error.message);
    }
  },

  // Register new user
  registerUser: async (userData) => {
    try {
      // Prepare registration data according to new API specification
      const registrationData = {
        phone: userData.phoneNumber,
        firstName: userData.name ? userData.name.split(' ').slice(0, -1).join(' ') : '',
        lastName: userData.name ? userData.name.split(' ').slice(-1)[0] : userData.name || '',
        name: userData.name,
        email: null, // Optional field
        organizationId: userData.organizationId || ORGANIZATION_ID,
        address: null, // Optional field
        avatar: userData.avatar || null,
        f1Count: 0,
        parentId: userData.parentId || null,
        phoneParent: userData.supervisorPhone || null,
        nameParent: userData.supervisorName || null,
        facebook: userData.facebook || null
      };

      console.log('Registering user with data:', registrationData);

      const response = await fetchWithTimeout(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData)
      });

      if (response.ok) {
        // 2xx status codes - Success
        const data = await response.json();
        console.log('Registration successful:', data);
        
        // Extract user info from response
        const userInfo = data.data || data;
        
        return { 
          success: true, 
          userId: userInfo?.id || Date.now(),
          user: userInfo,
          userData: data,
          message: 'Đăng ký thành công'
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Registration error:', errorData);
        throw new Error(`Registration failed (${response.status}): ${errorData.message || 'Đăng ký thất bại'}`);
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error('Có lỗi xảy ra khi đăng ký: ' + error.message);
    }
  },

  // Additional utility methods
  validateImageFile: (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'Chỉ chấp nhận file ảnh (JPG, PNG, GIF, WebP)'
      };
    }

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: 'File ảnh không được vượt quá 5MB'
      };
    }

    return {
      isValid: true,
      error: null
    };
  },

  // Check campaign participation eligibility
  checkCampaignEligibility: async (phoneNumber, campaignCode, organizationId = null) => {
    try {
      console.log('Checking campaign eligibility for phone:', phoneNumber, 'campaign:', campaignCode, 'organizationId:', organizationId || ORGANIZATION_ID);
      
      // Use new API endpoint: GET /api/campaigns/{code}/check-user
      const response = await fetchWithTimeout(`${API_BASE_URL}/campaigns/${campaignCode}/check-user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Campaign eligibility check response:', data);
        
        // Check if user exists in campaign participants
        // The API should return whether the user is eligible or not
        const isEligible = data.isEligible || data.eligible || data.exists || data.userExists || false;
        
        console.log('User eligibility check result:', { isEligible, phoneNumber, data });
        
        return {
          success: true,
          isEligible: isEligible,
          data: data,
          message: isEligible ? 'User is eligible for campaign' : 'User is not eligible for campaign'
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Check campaign eligibility error:', errorData);
        throw new Error(`Không thể kiểm tra điều kiện tham gia: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Check campaign eligibility network error:', error);
      throw new Error('Không thể kết nối đến server để kiểm tra điều kiện tham gia.');
    }
  },

  // Check if user is registered for campaign participation
  checkCampaignParticipation: async (phoneNumber, campaignCode = 'cd2') => {
    try {
      console.log('Checking campaign participation for phone:', phoneNumber, 'campaign:', campaignCode);
      
      // Use API endpoint: GET /api/campaigns/{code}/check-user?userCode={phoneNumber}
      const response = await fetchWithTimeout(`${API_BASE_URL}/campaigns/${campaignCode}/check-user?userCode=${phoneNumber}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Campaign participation check response:', data);
        
        // Check if user exists in campaign participants list
        const isRegistered = data.data?.isExist || false;
        
        console.log('User participation check result:', { isRegistered, phoneNumber, campaignCode, data });
        
        return {
          success: true,
          isRegistered: isRegistered,
          data: data,
          message: isRegistered ? 'User is registered for campaign' : 'User is not registered for campaign'
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Check campaign participation error:', errorData);
        throw new Error(`Không thể kiểm tra đăng ký tham gia: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Check campaign participation network error:', error);
      throw new Error('Không thể kết nối đến server để kiểm tra đăng ký tham gia.');
    }
  },

  // Get campaign information by campaign code
  getCampaignInfo: async (campaignCode) => {
    try {
      console.log('Getting campaign info for code:', campaignCode);
      
      const response = await fetchWithTimeout(`${API_BASE_URL}/campaigns/${campaignCode}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Campaign info response:', data);
        
        // Extract campaign object
        const campaign = data.campaign || data.data || data;
        
        // Extract URLs from multiple possible locations
        const mainUrl = data.mainUrl || data.main_url || data.formUrl || data.form_url || 
                       campaign?.mainUrl || campaign?.main_url || campaign?.formUrl || campaign?.form_url;
        const successUrl = data.successUrl || data.success_url || data.resultUrl || data.result_url ||
                          campaign?.successUrl || campaign?.success_url || campaign?.resultUrl || campaign?.result_url;
        
        console.log('Extracted URLs:', { mainUrl, successUrl, campaign });
        
        return {
          success: true,
          campaign: campaign,
          mainUrl: mainUrl,
          successUrl: successUrl
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get campaign info error:', errorData);
        throw new Error(`Không thể lấy thông tin campaign: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get campaign info network error:', error);
      throw new Error('Không thể kết nối đến server để lấy thông tin campaign.');
    }
  },

  // Get campaigns for a specific user
  getCampaignsForUser: async (phoneNumber, organizationId = null) => {
    try {
      console.log('Getting campaigns for user phone:', phoneNumber, 'organizationId:', organizationId || ORGANIZATION_ID);
      
      const response = await fetchWithTimeout(`${API_BASE_URL}/campaigns/user/${phoneNumber}/campaigns?organizationId=${organizationId || ORGANIZATION_ID}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('User campaigns response:', data);
        
        // Extract campaigns array
        const campaigns = data.data || data.campaigns || data || [];
        
        console.log('Extracted campaigns:', campaigns);
        
        return {
          success: true,
          campaigns: campaigns,
          statusCode: data.statusCode || 200
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get user campaigns error:', errorData);
        throw new Error(`Không thể lấy danh sách chiến dịch: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get user campaigns network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách chiến dịch.');
    }
  },

  // Get subordinates for a specific user - Returns hierarchical tree structure
  getSubordinates: async (phoneNumber) => {
    try {
      console.log('Getting subordinates for user phone:', phoneNumber);
      
      const response = await fetchWithTimeout(`${API_BASE_URL}/users/subordinates?userCode=${phoneNumber}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Subordinates response:', data);
        
        // New API response structure: nested hierarchy with children arrays
        const hierarchyData = data.data?.data?.hierarchy || data.data?.hierarchy || data.hierarchy || [];
        const userInfo = {
          userCode: data.data?.data?.userCode || data.data?.userCode || data.userCode,
          userName: data.data?.data?.userName || data.data?.userName || data.userName,
          level: data.data?.data?.level || data.data?.level || data.level,
          totalDescendants: data.data?.data?.totalDescendants || data.data?.totalDescendants || data.totalDescendants || 0
        };
        
        // Flatten hierarchy to get all members for grid view
        const flattenHierarchy = (nodes) => {
          let result = [];
          for (const node of nodes) {
            result.push(node);
            if (node.children && node.children.length > 0) {
              result = result.concat(flattenHierarchy(node.children));
            }
          }
          return result;
        };
        
        const allMembers = flattenHierarchy(hierarchyData);
        const total = userInfo.totalDescendants || allMembers.length;
        
        console.log('Extracted hierarchical data:', { 
          hierarchyData, 
          allMembers: allMembers.length, 
          total, 
          userInfo 
        });
        
        return {
          success: true,
          subordinates: allMembers, // Flattened list for grid view
          hierarchy: hierarchyData, // Tree structure for tree view
          total: total,
          userInfo: userInfo,
          statusCode: data.statusCode || 200
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get subordinates error:', errorData);
        throw new Error(`Không thể lấy danh sách thành viên: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get subordinates network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách thành viên.');
    }
  },

  // Get campaign points detail for authenticated user
  getCampaignPointsDetail: async (accessToken) => {
    try {
      console.log('Getting campaign points detail with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');
      
      if (!accessToken) {
        throw new Error('Access token is required for points API');
      }
      
      const response = await fetchWithTimeout(`${API_BASE_URL}/users/campaign-points-detail`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Campaign points detail response:', data);
        
        // Extract points data from response
        const pointsData = data.data || data;
        const totalPoints = pointsData.totalPoints || 0;
        const campaignCount = pointsData.campaignCount || 0;
        const campaignDetails = pointsData.campaignDetails || [];
        
        return {
          success: true,
          totalPoints: totalPoints,
          campaignCount: campaignCount,
          campaignDetails: campaignDetails,
          data: pointsData
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get campaign points error:', errorData);
        throw new Error(`Không thể lấy thông tin điểm: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get campaign points network error:', error);
      throw new Error('Không thể kết nối đến server để lấy thông tin điểm.');
    }
  },

  // Save user address for gift delivery
  saveUserAddress: async (phoneNumber, addressData) => {
    try {
      console.log('Saving user address for phone:', phoneNumber, 'address:', addressData);
      
      const profilePayload = {
        city: addressData.province,
        district: addressData.district,
        ward: addressData.ward,
        address: addressData.streetAddress,
        name: addressData.fullName || phoneNumber, // Use fullName from form or phone number as fallback
        phone: addressData.phone || phoneNumber // Include phone field from form
      };

      console.log('API payload being sent:', profilePayload);

      const headers = createHeaders();
      
      const response = await fetchWithTimeout(`${API_BASE_URL}/users/profile`, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(profilePayload)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('User profile updated successfully:', data);
        
        return {
          success: true,
          data: data,
          message: 'Địa chỉ đã được lưu thành công'
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Save address error:', errorData);
        throw new Error(`Không thể lưu địa chỉ (${response.status}): ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Save address network error:', error);
      throw new Error('Có lỗi xảy ra khi lưu địa chỉ: ' + error.message);
    }
  },

  // Format phone number for API
  formatPhoneNumber: (phone) => {
    // Remove all non-digits
    const cleaned = phone.replace(/\D/g, '');
    
    // Ensure it starts with 0 for Vietnamese numbers
    if (cleaned.length === 10 && cleaned.startsWith('0')) {
      return cleaned;
    } else if (cleaned.length === 9) {
      return '0' + cleaned;
    } else if (cleaned.length === 12 && cleaned.startsWith('84')) {
      return '0' + cleaned.substring(2);
    }
    
    return cleaned;
  }
};

export default API;
